import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronLeft, ChevronRight, X, BedDouble } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, parseISO } from "date-fns";

const REASON_COLORS = {
  Holiday: "bg-emerald-200 text-emerald-900 border-emerald-300",
  Sick: "bg-red-200 text-red-900 border-red-300",
  Training: "bg-sky-200 text-sky-900 border-sky-300",
  Personal: "bg-amber-200 text-amber-900 border-amber-300",
  TOIL: "bg-violet-200 text-violet-900 border-violet-300",
  Other: "bg-slate-200 text-slate-800 border-slate-300",
};

// Distinct visual for holiday-module rows so approved vs pending vs auto
// (Christmas closure) are all readable at a glance.
const HOLIDAY_STYLE = {
  approved: "bg-emerald-300 text-emerald-950 border-emerald-500",
  pending: "bg-amber-100 text-amber-900 border-amber-400 border-dashed",
  auto: "bg-violet-200 text-violet-950 border-violet-400 [background-image:repeating-linear-gradient(45deg,rgba(255,255,255,0.35)_0_4px,transparent_4px_8px)]",
};

function cellStyleFor(a) {
  if (!a) return "hover:bg-sky-50";
  if (a.source === "holiday" && a.holiday_status) {
    return HOLIDAY_STYLE[a.holiday_status] || REASON_COLORS[a.reason] || REASON_COLORS.Other;
  }
  return REASON_COLORS[a.reason] || REASON_COLORS.Other;
}

function cellLabel(a) {
  if (!a) return "";
  if (a.source === "holiday") {
    if (a.holiday_status === "pending") return "?";
    if (a.holiday_status === "auto") return "X";  // eXmas
    return a.reason?.[0] || "H";
  }
  return a.reason?.[0] || "•";
}

export default function StaffCalendar({ isStaff }) {
  const [absences, setAbsences] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reasons, setReasons] = useState(["Holiday", "Sick", "Training", "Personal", "Other"]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ user_id: "", start_date: "", end_date: "", reason: "Holiday", notes: "" });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const fetchAll = useCallback(async () => {
    try {
      const startAfter = format(monthStart, "yyyy-MM-dd");
      const startBefore = format(monthEnd, "yyyy-MM-dd");
      const { data } = await api.get(`/staff-calendar?start_after=${startAfter}&start_before=${startBefore}`);
      setAbsences(data.absences || []);
      setStaff(data.staff || []);
      if (data.reasons?.length) setReasons(data.reasons);
    } catch (err) { console.error("Failed to load staff calendar:", err); }
    finally { setLoading(false); }
  }, [monthStart, monthEnd]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = (prefilDate = "", userId = "") => {
    setEditing(null);
    setForm({ user_id: userId, start_date: prefilDate, end_date: prefilDate, reason: "Holiday", notes: "" });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ user_id: a.user_id, start_date: a.start_date, end_date: a.end_date, reason: a.reason, notes: a.notes || "" });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { user_id: _u, ...update } = form;
        await api.put(`/staff-calendar/${editing.id}`, update);
        toast.success("Absence updated");
      } else {
        if (!form.user_id) { toast.error("Choose a staff member"); return; }
        await api.post("/staff-calendar", form);
        toast.success("Absence added");
      }
      setDialogOpen(false); fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.reason} for ${a.user_name} (${a.start_date} → ${a.end_date})?`)) return;
    try {
      await api.delete(`/staff-calendar/${a.id}`);
      toast.success("Removed"); fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const absForStaffOnDay = (uid, day) => {
    const iso = format(day, "yyyy-MM-dd");
    return absences.find(a => a.user_id === uid && a.start_date <= iso && iso <= a.end_date);
  };

  return (
    <div className="space-y-4" data-testid="staff-calendar">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BedDouble size={16} className="text-muted-foreground" />
          <h3 className="font-heading font-semibold text-sm">Staff Calendar</h3>
          <Badge variant="outline" className="text-[10px]">{staff.length} staff</Badge>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="staff-cal-prev"><ChevronLeft size={16} /></button>
          <span className="text-sm font-medium min-w-[140px] text-center" data-testid="staff-cal-label">{format(currentMonth, "MMMM yyyy")}</span>
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="staff-cal-next"><ChevronRight size={16} /></button>
          <button onClick={() => setCurrentMonth(new Date())} className="text-[11px] px-2 py-1 rounded border border-border hover:bg-slate-100 text-muted-foreground ml-1">Today</button>
          {isStaff && (
            <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800 text-white ml-2" onClick={() => openCreate()} data-testid="add-staff-absence-btn">
              <Plus size={14} className="mr-1" /> Add Absence
            </Button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 px-1 text-[10px] text-muted-foreground items-center">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-emerald-300 border-emerald-500" />Holiday · approved</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border border-dashed bg-amber-100 border-amber-400" />Holiday · pending</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-violet-200 border-violet-400 [background-image:repeating-linear-gradient(45deg,rgba(255,255,255,0.5)_0_2px,transparent_2px_4px)]" />Xmas auto-booked</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-red-200 border-red-300" />Sick</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-sky-200 border-sky-300" />Training</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-violet-200 border-violet-300" />TOIL</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border bg-amber-200 border-amber-300" />Personal</span>
        {isStaff && <span className="italic ml-auto hidden sm:inline">Click a cell to add · pending & auto rows come from the Holidays module</span>}
      </div>

      <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="staff-cal-grid">
        <div className="min-w-[800px]">
          {/* Header row */}
          <div className="grid sticky top-0 bg-white z-10" style={{ gridTemplateColumns: `180px repeat(${days.length}, minmax(28px,1fr))` }}>
            <div className="px-3 py-2 text-[10px] font-medium text-muted-foreground border-b border-r border-border">Staff</div>
            {days.map(d => (
              <div key={d.toISOString()} className="text-center text-[10px] text-muted-foreground border-b border-r border-border py-1">
                <div>{format(d, "EE")[0]}</div>
                <div className="font-medium text-foreground">{format(d, "d")}</div>
              </div>
            ))}
          </div>
          {/* Staff rows */}
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading...</div>
          ) : staff.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No active admin/staff users.</div>
          ) : staff.map(s => (
            <div key={s.id} className="grid border-b border-border" style={{ gridTemplateColumns: `180px repeat(${days.length}, minmax(28px,1fr))` }}>
              <div className="px-3 py-2 border-r border-border bg-slate-50/40" data-testid={`staff-row-${s.id}`}>
                <div className="text-xs font-medium truncate">{s.name}</div>
                <div className="text-[9px] text-muted-foreground capitalize">{s.role}</div>
              </div>
              {days.map(d => {
                const a = absForStaffOnDay(s.id, d);
                const iso = format(d, "yyyy-MM-dd");
                const cls = cellStyleFor(a);
                const tooltipStatus = a?.source === "holiday" ? ` [${a.holiday_status || "holiday"}]` : "";
                return (
                  <button
                    key={iso}
                    onClick={() => {
                      if (!isStaff) return;
                      if (a && a.source === "absence") openEdit(a);
                      else if (!a) openCreate(iso, s.id);
                      // Holiday-module rows are read-only from the calendar —
                      // sending users to the Holidays page keeps approval
                      // state in a single place.
                    }}
                    title={a ? `${a.reason}${tooltipStatus}: ${a.start_date} → ${a.end_date}${a.notes ? ` (${a.notes})` : ""}` : `Add absence for ${s.name} on ${iso}`}
                    className={`border-r border-border min-h-[36px] transition-colors ${cls}`}
                    data-testid={`staff-cell-${s.id}-${iso}`}
                  >
                    <span className="text-[8px] font-bold leading-none">{cellLabel(a)}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Recent absences list */}
      {absences.length > 0 && (
        <div className="border border-border rounded-md bg-white">
          <div className="px-3 py-2 border-b border-border text-xs font-medium text-muted-foreground">This month — {absences.length} record{absences.length === 1 ? "" : "s"}</div>
          <div className="divide-y divide-border">
            {absences.map(a => {
              const holStatus = a.source === "holiday" ? a.holiday_status : "";
              return (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`staff-absence-row-${a.id}`}>
                  <Badge variant="outline" className={`text-[10px] ${REASON_COLORS[a.reason] || REASON_COLORS.Other}`}>{a.reason}</Badge>
                  {holStatus && (
                    <Badge variant="outline" className={`text-[10px] ${holStatus === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : holStatus === "pending" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-violet-100 text-violet-700 border-violet-200"}`}>
                      {holStatus === "auto" ? "Auto (Xmas)" : holStatus}
                    </Badge>
                  )}
                  <span className="font-medium">{a.user_name}</span>
                  <span className="text-muted-foreground text-xs">{a.start_date} → {a.end_date}</span>
                  {a.notes && <span className="text-muted-foreground text-xs truncate flex-1">· {a.notes}</span>}
                  {isStaff && a.source === "absence" && (
                    <button onClick={() => remove(a)} className="p-1 rounded hover:bg-red-50 text-red-500 ml-auto" data-testid={`remove-staff-absence-${a.id}`}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="staff-absence-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Absence" : "Add Absence"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Staff member</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="staff-absence-user-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="staff-absence-start" /></div>
              <div><Label className="text-xs">End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required data-testid="staff-absence-end" /></div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="staff-absence-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{reasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Notes (optional)</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="staff-absence-notes" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="staff-absence-submit">{editing ? "Update" : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
